import React, { useEffect } from 'react';
import './AppInfoModal.css';

const AppInfoModal = ({ app, isOpen, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !app) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-title-section">
            <div className="modal-icon">
              {app.category.icon}
            </div>
            <div>
              <h2 className="modal-title">{app.name}</h2>
              <div 
                className="modal-category-badge"
                style={{ backgroundColor: app.category.color }}
              >
                {app.category.name}
              </div>
            </div>
          </div>
          <button 
            className="modal-close-button"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="info-section">
            <h3>Description</h3>
            <p>{app.description}</p>
          </div>

          {app.detailedDescription && (
            <div className="info-section">
              <h3>Detailed Overview</h3>
              <p>{app.detailedDescription}</p>
            </div>
          )}

          {app.technicalDetails && (
            <div className="info-section">
              <h3>Technical Implementation</h3>
              <p>{app.technicalDetails}</p>
            </div>
          )}

          {app.department && (
            <div className="info-section">
              <h3>Department & Status</h3>
              <div className="status-grid">
                <div className="status-item">
                  <strong>Department:</strong> {app.department}
                </div>
                {app.technology && (
                  <div className="status-item">
                    <strong>Technology:</strong> {app.technology}
                  </div>
                )}
                {app.status && (
                  <div className="status-item">
                    <strong>Status:</strong> 
                    <span className={`status-badge ${app.status.toLowerCase()}`}>
                      {app.status}
                    </span>
                  </div>
                )}
                {app.deployed !== undefined && (
                  <div className="status-item">
                    <strong>Deployed:</strong> 
                    <span className={`deployment-status ${app.deployed ? 'deployed' : 'not-deployed'}`}>
                      {app.deployed ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                {app.usedBy && (
                  <div className="status-item">
                    <strong>Used By:</strong> {app.usedBy}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="info-section">
            <h3>Application URL</h3>
            <div className="url-container">
              <a 
                href={app.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="app-url"
              >
                {app.url}
              </a>
              <button 
                className="copy-url-button"
                onClick={() => navigator.clipboard.writeText(app.url)}
                title="Copy URL"
              >
                Copy
              </button>
            </div>
          </div>

          {app.tags && app.tags.length > 0 && (
            <div className="info-section">
              <h3>Tags</h3>
              <div className="modal-tags">
                {app.tags.map((tag, index) => (
                  <span key={index} className="modal-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {app.documentReference && (
            <div className="info-section">
              <h3>Documentation</h3>
              <div className="document-reference">
                <span className="document-icon">📄</span>
                <span>Reference: {app.documentReference}</span>
              </div>
            </div>
          )}

          {app.featured && (
            <div className="info-section">
              <div className="featured-indicator">
                <span className="featured-star">★</span>
                <span>Featured Application</span>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="modal-button secondary"
            onClick={onClose}
          >
            Close
          </button>
          <button 
            className="modal-button primary"
            onClick={() => window.open(app.url, '_blank', 'noopener,noreferrer')}
          >
            Launch App
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppInfoModal;